import React, { useState } from 'react';
import { todayISO } from '../../domain/clock';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import TransactionTypePicker from './transactions/TransactionTypePicker';
import TransactionForm from './transactions/TransactionForm';
import { useLedger } from '../../data/useLedger';
import { useStudents } from '../../data/useStudents';
import { useFamilies } from '../../data/useFamilies';
import { useNotifications } from '../../data/useNotifications';
import { paymentReceiptRequest } from '../../domain/notifications';
import { freeId } from '../../domain/freeId';
import { amountStep, clampAmountDecimals } from '../../domain/money';
import {
  emptyTransactionForm, familyPickerOrder, receiptRequested, transactionDrawerTitle,
  transactionFormAfterAddAnother, transactionFormErrors, transactionInput,
} from '../../domain/familyAccount';
import { useCurrency } from '../../context/LocaleContext';
import { familyDisplayName } from '../../domain/people';
import Field from '../Field';
import s from './transactions/transaction.module.css';

// Add Transaction (SPEC.md §3.3). Pick a kind, then fill it in — assembly only.
//
// Opened from a family (FamiliesPage passes `familyId`) it writes to that
// family. Opened without one — the "Add Family Transaction" choice on Expenses
// & Revenue (ExpensesPage.jsx, not an MMS control) — it asks which family
// first, as a required field: a ledger entry with no family is not a thing the
// ledger holds.

// A blank entry dated today, and what Save & Add Another keeps, are
// `src/domain/familyAccount.js`'s.
const emptyForm = () => emptyTransactionForm(todayISO());

export default function AddTransactionDrawer({ open, onClose, familyId = null }) {
  const { appendEntry } = useLedger();
  const { forFamily } = useStudents();
  const { byId: familyById, contactsFor, families = [] } = useFamilies();
  // Only consulted when no family was passed in.
  const [pickedFamilyId, setPickedFamilyId] = useState('');
  const choosesFamily = !familyId;
  const activeFamilyId = familyId || pickedFamilyId || null;
  const { messageBatch } = useNotifications();

  const currency = useCurrency();
  const [kind, setKind] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // Whether Save has been pressed on this entry. MMS shows its required-field
  // sentence at submit; this is what holds that "submitted once" state, and it
  // resets with the form so Save & Add Another does not greet the next entry
  // with a red field nobody has touched.
  const [attempted, setAttempted] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const students = activeFamilyId ? forFamily(activeFamilyId) : [];
  const familyOptions = choosesFamily
    ? familyPickerOrder(families.map((f) => familyDisplayName(f))).map((i) => families[i])
    : [];

  // ── What Save refuses, and how it says so (#180) ──────────────────────────
  //
  // As MMS's Add Transaction → Payment: with Amount cleared, the field outlines
  // red and prints "This field is required", Save stays enabled, and clicking
  // Save refuses at submit — the drawer stays open, nothing is written. MMS
  // disables Save only for a form that is unchanged, never for a value that is
  // wrong. So the button says nothing about whether the save will go through,
  // and the sentence under the field does; the submit guard below is the
  // refusal (`useExpenseEntryForm.js` has the argument beside `entryErrors`).
  //
  // The sentence shows at submit. Whether MMS shows it on blur alone is
  // unchecked.
  //
  // `amountError` is computed here rather than in TransactionForm because this
  // component has the currency the figure is typed in. `validLedgerEntry` bounds
  // `amountMinor` at MAX_MONEY_MINOR (#164).
  //
  // The refusal — both sentences and whether Save goes through — is
  // `transactionFormErrors` in src/domain/familyAccount.js.
  const errors = transactionFormErrors({
    amount: form.amount, familyId: activeFamilyId, choosesFamily, attempted,
  }, currency);
  const amountErrorText = errors.amount;
  const familyErrorText = errors.family;

  // The drawer stays mounted while it is closed, so closing is what empties
  // it; otherwise the next + Add Transaction would open on the last entry's
  // amount, description, date and receipt tick.
  const close = () => {
    setKind(null); setError(null); setAttempted(false); setPickedFamilyId(''); setForm(emptyForm()); onClose?.();
  };

  const save = async (addAnother = false) => {
    // The whole refusal: Save is live whatever this says (#180), so an empty or
    // over-large amount is stopped here and reported at the field.
    setAttempted(true);
    if (errors.refused) return;
    setSaving(true);
    setError(null);
    try {
      // The amount is parsed in the studio's currency, not dollars: a typed
      // "3000" is 3000 minor units in yen and 300000 in dollars, and the
      // ledger only ever holds minor units. See `transactionDocument`.
      const input = transactionInput({
        kind,
        amount: form.amount,
        date: form.date,
        familyId: activeFamilyId,
        studentId: form.studentId,
        description: form.description,
      }, currency);
      // The entry's id is minted here rather than inside `appendEntry`, so the
      // receipt can be keyed on it before anything is written — the phones'
      // order already (`AddTransactionScreen.kt`).
      const entryId = freeId(input.kind);
      // Queued against the entry's own id, so recording the same payment twice
      // asks for one receipt rather than two (domain/notifications.js).
      //
      // Every contact on the family, not the billing one (#413): MMS's own
      // composer resolves the account's audience as all of them and BCCs. The
      // request is `paymentReceiptRequest` in domain/notifications.js.
      //
      // The payment's follow-on, not a second commit (#592): written onto the
      // payment batch's durable note at Save and queued when the payment is
      // acknowledged, never if it is refused, so a page load in between cannot
      // drop the receipt while the payment lands. #590 is the same for the
      // attendance drawer.
      const receipt = receiptRequested({ kind, emailReceipt: form.emailReceipt, entryId })
        ? messageBatch([paymentReceiptRequest({
          family: familyById(activeFamilyId),
          contacts: contactsFor(activeFamilyId),
          familyId: activeFamilyId,
          entryId,
          occurredOn: form.date,
        })])
        : null;
      await appendEntry({ ...input, id: entryId }, { followOn: receipt?.batch ?? null });
      if (addAnother) {
        setForm(transactionFormAfterAddAnother);
        // The next entry has not been submitted, so the emptied Amount is not
        // yet a refusal — see `attempted` above.
        setAttempted(false);
      } else close();
    } catch (err) {
      // Money write. A failure has to be visible.
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const footer = kind ? (
    <>
      <Button variant="secondary" onClick={() => { setKind(null); setAttempted(false); }}>Back</Button>
      <FooterSpacer />
      {/* Neither button is disabled for a missing or invalid amount (#180).
          `loading` takes them out while a write is in flight, a different
          question. */}
      <Button variant="secondary" onClick={() => save(true)} loading={saving}>
        Save &amp; Add Another
      </Button>
      <Button onClick={() => save(false)} loading={saving}>
        Save
      </Button>
    </>
  ) : (
    <>
      <FooterSpacer />
      <Button variant="secondary" onClick={close}>Cancel</Button>
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={close}
      title={transactionDrawerTitle(kind)}
      footer={footer}
    >
      {error ? <div className={s.error}>Could not save: {error}</div> : null}

      {kind && choosesFamily ? (
        <div className={s.body}>
          <Field label="Family" error={familyErrorText}>
            <select
              className={s.input}
              value={pickedFamilyId}
              onChange={(e) => { setPickedFamilyId(e.target.value); set('studentId', ''); }}
            >
              <option value="">Choose a family</option>
              {familyOptions.map((f) => (
                <option key={f.id} value={f.id}>{familyDisplayName(f)}</option>
              ))}
            </select>
          </Field>
        </div>
      ) : null}

      {kind ? (
        <TransactionForm
          kind={kind} form={form} set={set} students={students}
          amountError={amountErrorText}
          // #246: MMS's Amount field swallows a third decimal outright, so the
          // text is cut to the currency's places on the way into state and the
          // field cannot hold one. See clampAmountDecimals in domain/money.js.
          setAmount={(v) => set('amount', clampAmountDecimals(v, currency))}
          amountStep={amountStep(currency)}
        />
      ) : (
        <TransactionTypePicker onPick={setKind} />
      )}
    </Drawer>
  );
}
